// sessionsModel.js — G2-WS1-T02 (refactor-deepening-r2 / G2, sessions domain).
//
// Deep module owning the two real INVARIANTS of a code session's lifecycle that
// createCodeSession was carrying inline:
//   1. the BIRTH CONTRACT — what a new session inherits (ui defaults) and the strict
//      profile scoping (profile_id ?? the active persona). §Arquitetura cita
//      "profile_id estrito" como o invariante que torna este domínio DEEP.
//   2. the ACTIVATION transition — a freshly created session is prepended, takes over
//      the workspace (a fresh full-width pane when empty, else it rebinds the focused
//      pane), and its arrival resets the global plan_mode chip. This mirrors
//      panesModel.setActiveSession's plan_mode reset on activation (the SAME invariant,
//      now at the inline-create site too).
//
// Deletion test (01-DOCTRINE): delete this and the ui-default inheritance + profile_id
// stamp + the prepend/pane-rebind/plan_mode-reset transition scatter back into the
// 137-line createCodeSession god-action. It concentrates "what a session IS at birth and
// how it takes over the workspace" behind two pure functions. The trivial array setters
// (updateCodeSession's sessions.map, delete's sessions.filter) and the workspace
// reconciliation (already owned by panesModel.applyWorkspaceState) are intentionally NOT
// here — they are shallow / already-deep. DEEP where there is an invariant, inline where
// there is not.
//
// applyWorkspaceState (from panesModel) persists the pane geometry to localStorage; this
// module composes it, matching the panesModel/openSessionInWorkspace pattern (renderer
// store model, not a window-free lib engine).

import { makePaneId, applyWorkspaceState } from './panesModel.js';

/**
 * The BIRTH CONTRACT: build the POST payload for a new code session, inheriting the ui
 * defaults for the unspecified fields and stamping the profile scope (explicit input
 * wins; else the active persona). Pure.
 *
 * @param {object} input  the caller's args (name required; the rest optional overrides)
 * @param {object} ui  code.ui (scope/branch/mode/permissions_mode/model defaults)
 * @param {string|null} profileId  the active profile id (injected — activeProfileId())
 * @returns {object} the /api/code/sessions POST body
 */
export function buildCreateSessionPayload(input, ui, profileId) {
  return {
    name: input.name,
    ...(input.initial_message ? { initial_message: input.initial_message } : {}),
    ...(input.directory ? { directory: input.directory } : {}),
    description: input.description ?? '',
    scope: input.scope ?? ui.scope,
    // Superfície de 1ª classe (Worker 0097). Default 'code'; a aba Chat passa 'chat'.
    kind: input.kind ?? 'code',
    branch: input.branch ?? ui.branch,
    mode: input.mode ?? ui.mode,
    permissions_mode: input.permissions_mode ?? ui.permissions_mode,
    model: input.model ?? ui.model,
    agent_id: input.agent_id ?? null,
    ...(input.client_intent_id ? { client_intent_id: input.client_intent_id } : {}),
    // super-marathon E3+ — stamp the active persona so the session scopes to it.
    profile_id: input.profile_id ?? profileId,
    ...(input.flags ? { flags: input.flags } : {}),
  };
}

/**
 * The ACTIVATION transition: fold a freshly created session into `code` — prepend it,
 * take over the workspace (fresh full-width pane when there are none, else rebind the
 * focused pane to the new session), and reset the global plan_mode chip (a new session
 * starts in chat mode; it must not inherit the sticky Plan chip). Returns the `code`
 * patch the store action spreads. Pure over `code` (applyWorkspaceState persists panes).
 *
 * @param {object} code  the current state.code
 * @param {object} session  the created session row
 * @returns {object} patch to merge into code
 */
export function insertCreatedSession(code, session) {
  const sessions = [session, ...code.sessions.filter((existing) => existing.id !== session.id)];
  const panes = code.workspacePanes || [];
  let panePatch;
  if (panes.length === 0) {
    const pane = { id: makePaneId(), sessionId: session.id, sizeFraction: 1 };
    panePatch = applyWorkspaceState([pane], pane.id);
  } else {
    const focusedId = code.focusedPaneId || panes[0]?.id;
    const nextPanes = panes.map((p) => (p.id === focusedId ? { ...p, sessionId: session.id } : p));
    panePatch = applyWorkspaceState(nextPanes, focusedId);
  }
  // #11 — sessão NOVA começa em chat mode: não herda o chip Plan global.
  const ui = code.ui?.plan_mode ? { ...code.ui, plan_mode: false } : code.ui;
  return { sessions, ...panePatch, ui };
}
